import { cva, cx } from '@repo/panda/css';
import type { RecipeVariantProps } from '@repo/panda/types';
import type React from 'react';
import { forwardRef, type HTMLAttributes } from 'react';

const fieldsetStyles = cva({
    base: {
        border: 'none',
        margin: '0',
        padding: '0',
        display: 'flex',
        flexDirection: 'column',
    },
    variants: {
        spacing: {
            xs: { gap: '4' },
            sm: { gap: '8' },
            md: { gap: '12' },
            lg: { gap: '16' },
        },
    },
    defaultVariants: {
        spacing: 'sm',
    },
});

const legendStyles = cva({
    base: {
        padding: '0',
        marginRight: '4',
        fontSize: 'md',
        fontWeight: 'medium',
        color: 'grey.300',
    },
    variants: {
        size: {
            xs: { fontSize: 'sm' },
            sm: { fontSize: 'md' },
            md: { fontSize: 'lg' },
        },
        hidden: {
            true: {
                position: 'absolute',
                width: '[1px]',
                height: '[1px]',
                padding: '0',
                margin: '[-1px]',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                border: 'none',
                clip: '[rect(0,0,0,0)]',
            },
        },
    },
    defaultVariants: {
        size: 'sm',
    },
});

export type FieldsetProps = HTMLAttributes<HTMLFieldSetElement> &
    RecipeVariantProps<typeof fieldsetStyles> & {
        children: React.ReactNode;
        legend?: string;
        legendHidden?: boolean;
        legendSize?: 'xs' | 'sm' | 'md';
        disabled?: boolean;
    };

export const Fieldset = forwardRef<HTMLFieldSetElement, FieldsetProps>(
    ({ children, spacing, legend, legendHidden = false, legendSize = 'sm', disabled, className, ...props }, ref) => {
        return (
            <fieldset ref={ref} className={cx(fieldsetStyles({ spacing }), className)} disabled={disabled} {...props}>
                {legend && (
                    <legend className={legendStyles({ size: legendSize, hidden: legendHidden })}>{legend}</legend>
                )}
                {children}
            </fieldset>
        );
    },
);
